import { z } from 'zod';
import { provenanceRecordSchema } from './agent.types.js';
import { modelLayoutSchema } from './model-layout.types.js';
/**
 * Canonical builder projection consumed by Workbench / Model / Signal tabs.
 * Replaces the historical {agent_state, model-view} dual-endpoint fan-in.
 * Served by `GET /api/conversations/:id/views` (Phase 4).
 *
 * `inputs` is the SSOT — every value the UI shows derives from `inputs[k]`
 * directly OR via `cellValues[<sheet>!<addr>]` (formula outputs). Per spec §3
 * + ADR-0001 (provenance as SSOT).
 *
 * `signal` is intentionally `z.unknown()` in P1 — the signal projection's
 * exact composed shape is deferred to P4 wiring (when the `/views` endpoint
 * composes from `signal.ts`'s existing typed schemas). Acceptable per spec
 * acceptance criterion API #1 ("Zod schema validates response shape").
 */
export const viewsResponseSchema = z.object({
    conversationId: z.string().uuid(),
    modelId: z.string(),
    modelVersion: z.string(),
    inputs: z.record(z.string(), provenanceRecordSchema), // SSOT — value + metadata
    cellValues: z.record(z.string(), z.unknown()), // engine-computed; key="<sheet>!<addr>"
    modelLayout: modelLayoutSchema.nullable(),
    signal: z.unknown(),
    dealMetadata: z
        .object({
        market: z.string().nullable().optional(),
        units: z.number().optional(),
        basis: z.string().optional(),
        targetIrr: z.string().optional(),
        targetYoc: z.string().optional(),
    })
        .passthrough()
        .nullable(),
    aerialLatLng: z.object({ lat: z.number(), lng: z.number() }).nullable(),
    meta: z.object({
        revision: z.number().int().nonnegative(),
        updatedAt: z.string().datetime(),
    }),
});
/**
 * Per-location resolution shape, optionally nested inside `dealMetadata` on
 * the agent state or `views` API responses. Authored as a small P1-backport
 * (builder-ssot-consolidation Phase 2 Session 2.0, ADR-0012) so the
 * agents-side `deriveDealMetadataLocation` helper can return a typed value
 * + P4's `viewsResponseSchema.dealMetadata` can later widen to a typed
 * nested-Location field against this same schema.
 *
 * All fields optional; `.passthrough()` preserves additional resolver-emitted
 * keys; `.nullable()` because the derive helper returns `null` when no
 * Location keys are present.
 */
export const dealMetadataLocationSchema = z
    .object({
    lat: z.number().optional(),
    lng: z.number().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    placeId: z.string().optional(),
    floodZone: z.string().optional(),
    formattedAddress: z.string().optional(),
})
    .passthrough()
    .nullable();
